import { type DynamicWidgetActionRequest } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_actions';
import { type DynamicWidgetDiagnosticRequest } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_diagnostic';
import {
    type PhoebeConversationSource,
    type ToolApprovalDecision,
    type ToolApprovalDecisionTarget,
    type ToolApprovalScope,
} from '@apps/web/components/phoebe_chat_view/transcript_types';
import { getApiClient } from '@apps/web/lib/api_client';
import {
    type AgentApprovalMode,
    type AgentRunDebriefDecisionVerdict,
    ApiError,
    type components,
} from '@libraries/typescript/api_client';
import { generateUuid } from '@libraries/typescript/utilities';

export type ChatBackendKind =
    | 'run_native'
    | 'legacy_agent_conversation'
    | 'admin_trace_compat';

export type ChatMessageOrigin = NonNullable<
    components['schemas']['AgentRunMessageCreateRequest']['message_origin']
>;

export function getInitialChatBackendKind({
    conversationSource,
    usesAgentConversationEndpoints,
}: {
    readonly conversationSource: PhoebeConversationSource;
    readonly usesAgentConversationEndpoints: boolean;
}): ChatBackendKind {
    if (conversationSource === 'admin') {
        return 'admin_trace_compat';
    }
    if (usesAgentConversationEndpoints) {
        return 'legacy_agent_conversation';
    }
    return 'run_native';
}

export function getEffectiveChatBackendKind({
    initialBackendKind,
    shouldUseLegacyOutreachFallback,
}: {
    readonly initialBackendKind: ChatBackendKind;
    readonly shouldUseLegacyOutreachFallback: boolean;
}): ChatBackendKind {
    if (
        initialBackendKind === 'run_native' &&
        shouldUseLegacyOutreachFallback
    ) {
        return 'legacy_agent_conversation';
    }
    return initialBackendKind;
}

function requireId(value: string | null | undefined, name: string): string {
    if (value === null || value === undefined) {
        throw new Error(`Missing ${name}.`);
    }
    return value;
}

export function createChatBackendConversation({
    backendKind,
    organizationId,
    activeModel,
    approvalMode = null,
    runType = null,
    clientSurface = null,
}: {
    readonly backendKind: ChatBackendKind;
    readonly organizationId: string;
    readonly activeModel: string | null;
    readonly approvalMode?: AgentApprovalMode | null;
    readonly runType?: 'orchestrator' | null;
    readonly clientSurface?: string | null;
}) {
    const body =
        activeModel !== null
            ? {
                  title: 'New chat',
                  active_model: activeModel,
              }
            : { title: 'New chat' };

    if (backendKind === 'run_native') {
        const runBody = {
            ...body,
            ...(approvalMode !== null ? { approval_mode: approvalMode } : {}),
            ...(runType === 'orchestrator' ? { run_type: runType } : {}),
            ...(clientSurface !== null
                ? { client_surface: clientSurface }
                : {}),
        };

        return getApiClient().post({
            url: '/organizations/{organization_id}/runs',
            pathParams: { organization_id: organizationId },
            body: runBody,
        });
    }

    return getApiClient().post({
        url: '/organizations/{organization_id}/agent-conversations',
        pathParams: { organization_id: organizationId },
        body,
    });
}

export function ensureOutreachAnalysisRun({
    organizationId,
    outreachId,
}: {
    readonly organizationId: string;
    readonly outreachId: string;
}) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/runs/outreach-analysis/by-outreach/{outreach_id}',
        pathParams: {
            organization_id: organizationId,
            outreach_id: outreachId,
        },
    });
}

export function ensureOutreachRunnerRun({
    organizationId,
    outreachId,
}: {
    readonly organizationId: string;
    readonly outreachId: string;
}) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/runs/outreach-runner/by-outreach/{outreach_id}',
        pathParams: {
            organization_id: organizationId,
            outreach_id: outreachId,
        },
    });
}

export function resetOutreachRunnerRun({
    organizationId,
    outreachId,
    expectedRunnerRunId,
}: {
    readonly organizationId: string;
    readonly outreachId: string;
    readonly expectedRunnerRunId: string | null;
}) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/runs/outreach-runner/by-outreach/{outreach_id}/reset',
        pathParams: {
            organization_id: organizationId,
            outreach_id: outreachId,
        },
        body: { expected_runner_run_id: expectedRunnerRunId },
    });
}

// The reset endpoint refuses to fence a runner the caller was not looking
// at, which is how a second tab or teammate announces it got there first.
export function isOutreachRunnerLinkChangedError(error: unknown): boolean {
    if (!(error instanceof ApiError) || error.status !== 409) {
        return false;
    }
    const body: unknown = error.body;
    if (typeof body !== 'object' || body === null || !('detail' in body)) {
        return false;
    }
    const detail: unknown = (body as { readonly detail: unknown }).detail;
    return (
        typeof detail === 'object' &&
        detail !== null &&
        'code' in detail &&
        (detail as { readonly code: unknown }).code === 'runner_link_changed'
    );
}

export function updateChatBackendModel({
    backendKind,
    organizationId,
    runId,
    agentConversationId,
    adminTraceConversationId,
    activeModel,
}: {
    readonly backendKind: ChatBackendKind;
    readonly organizationId: string | null;
    readonly runId?: string | null;
    readonly agentConversationId?: string | null;
    readonly adminTraceConversationId?: string | null;
    readonly activeModel: string | null;
}) {
    if (backendKind === 'admin_trace_compat') {
        return getApiClient().patch({
            url: '/admin/agent-traces/{conversation_id}/model',
            pathParams: {
                conversation_id: requireId(
                    adminTraceConversationId,
                    'admin trace conversation id',
                ),
            },
            body: { active_model: activeModel },
        });
    }

    if (backendKind === 'run_native') {
        const requiredOrganizationId = requireId(
            organizationId,
            'organization id',
        );
        return getApiClient().patch({
            url: '/organizations/{organization_id}/runs/{run_id}/model',
            pathParams: {
                organization_id: requiredOrganizationId,
                run_id: requireId(runId, 'run id'),
            },
            body: { active_model: activeModel },
        });
    }

    return getApiClient().patch({
        url: '/organizations/{organization_id}/agent-conversations/{agent_conversation_id}/model',
        pathParams: {
            organization_id: requireId(organizationId, 'organization id'),
            agent_conversation_id: requireId(
                agentConversationId,
                'agent conversation id',
            ),
        },
        body: { active_model: activeModel },
    });
}

export function updateChatBackendApprovalMode({
    backendKind,
    organizationId,
    runId,
    approvalMode,
}: {
    readonly backendKind: ChatBackendKind;
    readonly organizationId: string | null;
    readonly runId?: string | null;
    readonly approvalMode: AgentApprovalMode | null;
}) {
    if (backendKind !== 'run_native') {
        throw new Error('Approval mode is only available on agent runs.');
    }

    return getApiClient().patch({
        url: '/organizations/{organization_id}/runs/{run_id}/approval-mode',
        pathParams: {
            organization_id: requireId(organizationId, 'organization id'),
            run_id: requireId(runId, 'run id'),
        },
        body: { approval_mode: approvalMode },
    });
}

export function resetLegacyAgentConversation({
    organizationId,
    agentConversationId,
}: {
    readonly organizationId: string;
    readonly agentConversationId: string;
}) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/agent-conversations/{agent_conversation_id}/reset',
        pathParams: {
            organization_id: organizationId,
            agent_conversation_id: agentConversationId,
        },
    });
}

export function sendChatBackendMessage({
    backendKind,
    organizationId,
    runId,
    agentConversationId,
    adminTraceConversationId,
    content,
    operationId,
    activityEventIds = [],
    uploadIds = [],
    messageOrigin,
    widgetAction,
    widgetDiagnostic,
}: {
    readonly backendKind: ChatBackendKind;
    readonly organizationId: string | null;
    readonly runId?: string | null;
    readonly agentConversationId?: string | null;
    readonly adminTraceConversationId?: string | null;
    readonly content: string;
    readonly operationId: string;
    readonly activityEventIds?: ReadonlyArray<string>;
    // Coordinator chat uploads previously POSTed to
    // `/phoebe_agent_runs/{run_id}/user-uploads`. The endpoint hydrates
    // each id into a `UserUploadRef` on the enqueued `UserMessageEvent`
    // and drops the workspace file for the agent's bash tools.
    readonly uploadIds?: ReadonlyArray<string>;
    readonly messageOrigin?: ChatMessageOrigin;
    readonly widgetAction?: DynamicWidgetActionRequest;
    readonly widgetDiagnostic?: DynamicWidgetDiagnosticRequest;
}) {
    if (backendKind === 'admin_trace_compat') {
        return getApiClient().post({
            url: '/admin/agent-traces/{conversation_id}/messages',
            pathParams: {
                conversation_id: requireId(
                    adminTraceConversationId,
                    'admin trace conversation id',
                ),
            },
            body: {
                content,
                client_operation_id: operationId,
            },
            requestId: operationId,
        });
    }

    if (backendKind === 'run_native') {
        const requiredOrganizationId = requireId(
            organizationId,
            'organization id',
        );
        return getApiClient().post({
            url: '/organizations/{organization_id}/runs/{run_id}/messages',
            pathParams: {
                organization_id: requiredOrganizationId,
                run_id: requireId(runId, 'run id'),
            },
            body: {
                content,
                client_operation_id: operationId,
                activity_event_ids: [...activityEventIds],
                ...(uploadIds.length > 0 ? { upload_ids: [...uploadIds] } : {}),
                ...(messageOrigin !== undefined
                    ? { message_origin: messageOrigin }
                    : {}),
                ...(widgetAction !== undefined
                    ? { widget_action: widgetAction }
                    : {}),
                ...(widgetDiagnostic !== undefined
                    ? { widget_diagnostic: widgetDiagnostic }
                    : {}),
            },
            requestId: operationId,
        });
    }

    return getApiClient().post({
        url: '/organizations/{organization_id}/agent-conversations/{agent_conversation_id}/messages',
        pathParams: {
            organization_id: requireId(organizationId, 'organization id'),
            agent_conversation_id: requireId(
                agentConversationId,
                'agent conversation id',
            ),
        },
        body: {
            content,
            client_operation_id: operationId,
            activity_event_ids: [...activityEventIds],
            ...(uploadIds.length > 0 ? { upload_ids: [...uploadIds] } : {}),
            ...(messageOrigin !== undefined
                ? { message_origin: messageOrigin }
                : {}),
            ...(widgetAction !== undefined
                ? { widget_action: widgetAction }
                : {}),
            ...(widgetDiagnostic !== undefined
                ? { widget_diagnostic: widgetDiagnostic }
                : {}),
        },
        requestId: operationId,
    });
}

export async function decideChatBackendToolCalls({
    backendKind,
    organizationId,
    runId,
    agentConversationId,
    adminTraceConversationId,
    target,
    decision,
    alwaysAllowScope,
    argumentsOverride,
    operationId,
}: {
    readonly backendKind: ChatBackendKind;
    readonly organizationId: string | null;
    readonly runId?: string | null;
    readonly agentConversationId?: string | null;
    readonly adminTraceConversationId?: string | null;
    readonly target: ToolApprovalDecisionTarget;
    readonly decision: ToolApprovalDecision;
    readonly alwaysAllowScope?: ToolApprovalScope;
    readonly argumentsOverride?: Record<string, unknown>;
    readonly operationId: string;
}) {
    const callIds = Array.isArray(target) ? [...target] : [target];
    if (callIds.length === 0) {
        throw new Error('At least one tool call is required.');
    }

    const canUseRunNativeBatch =
        backendKind === 'run_native' &&
        callIds.length > 1 &&
        decision !== 'always_allow' &&
        argumentsOverride === undefined &&
        (alwaysAllowScope === undefined || alwaysAllowScope === 'conversation');
    if (canUseRunNativeBatch) {
        const requiredOrganizationId = requireId(
            organizationId,
            'organization id',
        );
        return getApiClient().post({
            url: '/organizations/{organization_id}/runs/{run_id}/tool-decisions',
            pathParams: {
                organization_id: requiredOrganizationId,
                run_id: requireId(runId, 'run id'),
            },
            body: {
                call_ids: callIds,
                decision,
                always_allow_scope: 'conversation',
            },
            requestId: operationId,
        });
    }

    let resumed = false;
    for (const callId of callIds) {
        const requestId = callIds.length === 1 ? operationId : generateUuid();
        const result =
            backendKind === 'run_native'
                ? await getApiClient().post({
                      url: '/organizations/{organization_id}/runs/{run_id}/tool-decisions',
                      pathParams: {
                          organization_id: requireId(
                              organizationId,
                              'organization id',
                          ),
                          run_id: requireId(runId, 'run id'),
                      },
                      body: {
                          call_id: callId,
                          decision,
                          // Required by the generated request type; the server
                          // only reads it for always_allow decisions.
                          always_allow_scope:
                              alwaysAllowScope ?? 'conversation',
                          ...(argumentsOverride !== undefined
                              ? { arguments_override: argumentsOverride }
                              : {}),
                      },
                      requestId,
                  })
                : backendKind === 'admin_trace_compat'
                  ? await getApiClient().post({
                        url: '/admin/agent-traces/{conversation_id}/tool-decisions',
                        pathParams: {
                            conversation_id: requireId(
                                adminTraceConversationId,
                                'admin trace conversation id',
                            ),
                        },
                        body: {
                            call_id: callId,
                            decision,
                        },
                        requestId,
                    })
                  : await getApiClient().post({
                        url: '/organizations/{organization_id}/agent-conversations/{agent_conversation_id}/tool-decisions',
                        pathParams: {
                            organization_id: requireId(
                                organizationId,
                                'organization id',
                            ),
                            agent_conversation_id: requireId(
                                agentConversationId,
                                'agent conversation id',
                            ),
                        },
                        body: {
                            call_id: callId,
                            decision,
                            // Required by the generated request type; the server
                            // only reads it for always_allow decisions.
                            always_allow_scope:
                                alwaysAllowScope ?? 'conversation',
                            ...(argumentsOverride !== undefined
                                ? { arguments_override: argumentsOverride }
                                : {}),
                        },
                        requestId,
                    });
        resumed = resumed || result.resumed;
    }
    return { resumed };
}

export function submitRunQuestionAnswer({
    organizationId,
    runId,
    callId,
    payload,
    operationId,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly callId: string;
    readonly payload: {
        readonly answer?: string | Array<string> | null;
        readonly answers?: Array<{
            readonly question_id: string;
            readonly answer?: string | Array<string> | null;
            readonly skipped: boolean;
        }> | null;
        readonly cancelled?: boolean;
    };
    readonly operationId: string;
}) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/runs/{run_id}/question-answers',
        pathParams: {
            organization_id: organizationId,
            run_id: runId,
        },
        body: {
            call_id: callId,
            answer: payload.answer ?? null,
            answers: payload.answers ?? null,
            cancelled: payload.cancelled ?? false,
        },
        requestId: operationId,
    });
}

export function interruptChatBackendRun({
    backendKind,
    organizationId,
    runId,
    agentConversationId,
    adminTraceConversationId,
    operationId,
}: {
    readonly backendKind: ChatBackendKind;
    readonly organizationId: string | null;
    readonly runId?: string | null;
    readonly agentConversationId?: string | null;
    readonly adminTraceConversationId?: string | null;
    readonly operationId: string;
}) {
    if (backendKind === 'admin_trace_compat') {
        return getApiClient().post({
            url: '/admin/agent-traces/{conversation_id}/interrupt',
            pathParams: {
                conversation_id: requireId(
                    adminTraceConversationId,
                    'admin trace conversation id',
                ),
            },
            requestId: operationId,
        });
    }

    const requiredOrganizationId = requireId(organizationId, 'organization id');
    if (backendKind === 'run_native') {
        return getApiClient().post({
            url: '/organizations/{organization_id}/runs/{run_id}/interrupt',
            pathParams: {
                organization_id: requiredOrganizationId,
                run_id: requireId(runId, 'run id'),
            },
            requestId: operationId,
        });
    }

    return getApiClient().post({
        url: '/organizations/{organization_id}/agent-conversations/{agent_conversation_id}/interrupt',
        pathParams: {
            organization_id: requiredOrganizationId,
            agent_conversation_id: requireId(
                agentConversationId,
                'agent conversation id',
            ),
        },
        requestId: operationId,
    });
}

export function configureRunOutreachMonitoring({
    organizationId,
    runId,
    conditions,
    operationId,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly conditions: Array<string>;
    readonly operationId: string;
}) {
    const postConfigure = getApiClient().post as (args: {
        url: '/organizations/{organization_id}/runs/{run_id}/outreach-monitoring/configure';
        pathParams: {
            organization_id: string;
            run_id: string;
        };
        body: {
            conditions: Array<string>;
            client_operation_id: string;
        };
        requestId: string;
    }) => Promise<unknown>;
    return postConfigure({
        url: '/organizations/{organization_id}/runs/{run_id}/outreach-monitoring/configure',
        pathParams: {
            organization_id: organizationId,
            run_id: runId,
        },
        body: {
            conditions,
            client_operation_id: operationId,
        },
        requestId: operationId,
    });
}

export function stopRunOutreachMonitoring({
    organizationId,
    runId,
    operationId,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly operationId: string;
}) {
    const postStop = getApiClient().post as (args: {
        url: '/organizations/{organization_id}/runs/{run_id}/outreach-monitoring/stop';
        pathParams: {
            organization_id: string;
            run_id: string;
        };
        requestId: string;
    }) => Promise<unknown>;
    return postStop({
        url: '/organizations/{organization_id}/runs/{run_id}/outreach-monitoring/stop',
        pathParams: {
            organization_id: organizationId,
            run_id: runId,
        },
        requestId: operationId,
    });
}

/**
 * Rate one assistant answer. Omitting `comment` keeps whatever note is already
 * saved — only send it to replace one.
 */
export function saveMessageFeedback({
    organizationId,
    runId,
    agentItemId,
    label,
    comment,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly agentItemId: string;
    readonly label: 'good' | 'bad';
    readonly comment?: string;
}) {
    return getApiClient().put({
        url: '/organizations/{organization_id}/runs/{run_id}/message-feedback',
        pathParams: {
            organization_id: organizationId,
            run_id: runId,
        },
        body: {
            agent_item_id: agentItemId,
            label,
            ...(comment === undefined ? {} : { comment }),
        },
        reason: 'agent-message-feedback',
    });
}

export function saveRunFeedback({
    organizationId,
    runId,
    status,
    label,
    comment,
    assistantResponseCount,
    decisionVerdicts,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly status: 'submitted' | 'dismissed';
    readonly label?: 'good' | 'bad';
    readonly comment?: string;
    // Omit when the count is unknown (e.g. reviewing from the Teach Phoebe
    // feed); the backend then preserves any previously recorded count.
    readonly assistantResponseCount?: number;
    // Only the end-of-run debrief card sends these; omitting leaves any
    // saved verdicts untouched.
    readonly decisionVerdicts?: Array<AgentRunDebriefDecisionVerdict>;
}) {
    return getApiClient().put({
        url: '/organizations/{organization_id}/runs/{run_id}/feedback',
        pathParams: {
            organization_id: organizationId,
            run_id: runId,
        },
        body: {
            status,
            label,
            comment,
            assistant_response_count: assistantResponseCount,
            ...(decisionVerdicts === undefined
                ? {}
                : { decision_verdicts: decisionVerdicts }),
        },
        reason: 'agent-run-feedback',
    });
}

// Save a Teach Phoebe review on a voice call by writing the call's own rating
// (voice_call_ratings) — the single home for voice feedback, shared with the
// call detail page. In a training org a rating comment also feeds the
// agent-notes pipeline server-side, so voice feedback trains Phoebe the same
// way chat feedback does. Chat runs keep using saveRunFeedback.
export function saveVoiceCallRating({
    organizationId,
    voiceCallId,
    label,
    comment,
}: {
    readonly organizationId: string;
    readonly voiceCallId: string;
    readonly label: 'good' | 'bad';
    readonly comment?: string;
}) {
    return getApiClient().put({
        url: '/organizations/{organization_id}/voice-calls/{voice_call_id}/rating',
        pathParams: {
            organization_id: organizationId,
            voice_call_id: voiceCallId,
        },
        body: {
            label,
            comment,
        },
        reason: 'training-voice-rating',
    });
}

// Save a Teach Phoebe review on a single Phoebe SMS reply (its engine run) —
// coordinator-facing, keyed by the reply's run. In a training org a comment
// also feeds the agent-notes pipeline server-side, so SMS trains Phoebe the
// same way chat and voice do.
export function saveSmsRunRating({
    organizationId,
    runId,
    label,
    comment,
}: {
    readonly organizationId: string;
    readonly runId: string;
    readonly label: 'good' | 'bad';
    readonly comment?: string;
}) {
    return getApiClient().put({
        url: '/organizations/{organization_id}/message-ratings/runs/{run_id}/rating',
        pathParams: {
            organization_id: organizationId,
            run_id: runId,
        },
        body: {
            label,
            comment,
        },
        reason: 'training-sms-rating',
    });
}

// Clear a Teach Phoebe voice-call review (unclick), returning the call to
// unreviewed. Mirrors saveVoiceCallRating on the DELETE verb.
export function deleteVoiceCallRating({
    organizationId,
    voiceCallId,
}: {
    readonly organizationId: string;
    readonly voiceCallId: string;
}) {
    return getApiClient().delete({
        url: '/organizations/{organization_id}/voice-calls/{voice_call_id}/rating',
        pathParams: {
            organization_id: organizationId,
            voice_call_id: voiceCallId,
        },
        reason: 'training-voice-rating-clear',
    });
}

// Clear a Teach Phoebe SMS run review (unclick) by removing this user's
// run-level annotation, returning the run to unreviewed.
export function deleteSmsRunRating({
    organizationId,
    runId,
}: {
    readonly organizationId: string;
    readonly runId: string;
}) {
    return getApiClient().delete({
        url: '/organizations/{organization_id}/message-ratings/runs/{run_id}/rating',
        pathParams: {
            organization_id: organizationId,
            run_id: runId,
        },
        reason: 'training-sms-rating-clear',
    });
}
